/**
 * Utilities for handling @mentions in comments
 * Follows YouTube-style threading where nested replies are flattened with @mentions
 */

export interface MentionMatch {
  username: string;
  userId: string;
  start: number;
  end: number;
}

export interface ParsedComment {
  text: string;
  mentions: MentionMatch[];
}

// Simple regex to find @username mentions (supports alphanumeric, underscores, dots)
const MENTION_REGEX = /@([a-zA-Z0-9._]+)/g;

/**
 * Parse comment content to extract @mentions
 * @param content The raw comment content
 * @param availableUsers Map of username to user data for validation
 */
export function parseCommentMentions(
  content: string,
  availableUsers: Map<string, { id: string; username: string }>
): ParsedComment {
  const mentions: MentionMatch[] = [];
  let match;

  // Reset regex state
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(content)) !== null) {
    const username = match[1];
    const userData = availableUsers.get(username);

    if (userData) {
      mentions.push({
        username,
        userId: userData.id,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  }

  return {
    text: content,
    mentions,
  };
}

/**
 * Create a threaded reply with proper @mention for the user being replied to
 * @param replyingToUsername The username of the person being replied to
 * @param content The reply content
 */
export function createThreadedReply(
  replyingToUsername: string,
  content: string
): string {
  const trimmedContent = content.trim();

  // Check if the content already starts with the @mention
  const mentionPrefix = `@${replyingToUsername}`;
  if (trimmedContent.startsWith(mentionPrefix)) {
    return trimmedContent;
  }

  // Add the @mention at the beginning
  return `${mentionPrefix} ${trimmedContent}`;
}

/**
 * Build a map of available users from comments for mention validation
 * @param comments Array of comments with user data
 */
export function buildUserMap(
  comments: any[]
): Map<string, { id: string; username: string }> {
  const userMap = new Map<string, { id: string; username: string }>();
  const usernameCounts = new Map<string, number>();

  function processComment(comment: any) {
    // Skip if comment is null or undefined
    if (!comment) return;

    // Use username from API instead of deriving from email
    if (comment.users_extra?.username && comment.user_id) {
      const baseUsername = comment.users_extra.username;

      // Check if we've already seen this user ID to avoid duplicates
      const existingEntry = Array.from(userMap.values()).find(
        (u) => u.id === comment.user_id
      );
      if (!existingEntry) {
        // Handle username collisions by appending a number if needed
        let username = baseUsername;
        const count = usernameCounts.get(baseUsername) || 0;

        if (count > 0) {
          // This username already exists, add a number suffix
          username = `${baseUsername}${count + 1}`;
        }

        usernameCounts.set(baseUsername, count + 1);
        userMap.set(username, {
          id: comment.user_id,
          username: baseUsername, // Store the original username for display
        });
      }
    }

    // Process replies recursively
    if (comment.replies && Array.isArray(comment.replies)) {
      comment.replies.forEach(processComment);
    }
  }

  if (comments && Array.isArray(comments)) {
    comments.forEach(processComment);
  }

  return userMap;
}
